import * as React from "react";
import * as Sentry from "@sentry/react";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router";
import { useToggle } from "react-use";
import { toast } from "react-toastify";
import { BusinessReport, BusinessReportPrompt, VariablesDict } from "@/modules/snapshots/businessReports.types";
import { GenerateSnapshotCollectionModal } from "../modals/generateSnapshotCollectionModal";
import { Item } from "@/types/fse";
import { InjectVariablesModal } from "../modals/injectVariablesModal";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { useBusinessReportAdmin } from "@/hooks/use-business-report-admin";
import { useCreateBusinessReportOutcomeMutation } from "../businessReports.api";
import * as analytics from "@/services/analytics";

interface Props {
  selectedBusinessReport: BusinessReport;
  fsEntries: Item[];
  prompts: BusinessReportPrompt[];
}

const MAX_FILES = 20;

export const BusinessReportsPageGenerate: React.FC<Props> = ({ fsEntries, prompts, selectedBusinessReport }) => {
  const { selectedFiles } = useCurrentPdf();
  const selectedFsEntries = fsEntries.filter(row => selectedFiles.includes(row.id));
  const isUserAdmin = useBusinessReportAdmin();
  const navigate = useNavigate();
  const [createBusinessReportOutcome, { isLoading: isCreatingSnapshot }] = useCreateBusinessReportOutcomeMutation();

  const [isOpen, toggleIsOpen] = useToggle(false);
  const [showVariablesModal, setShowVariablesModal] = React.useState(false);
  const [variables, setVariables] = React.useState<VariablesDict>({});
  const createSingleSnapshot = async (newVariables: VariablesDict) => {
    const fsEntry = selectedFsEntries[0];
    if (!fsEntry) return;

    try {
      const result = await createBusinessReportOutcome({
        fs_entries: [fsEntry.id],
        business_report_id: selectedBusinessReport.id,
        business_report_prompts: prompts.map(prompt => ({
          order: prompt.order,
          full_context: prompt.full_context,
          business_report_prompt_id: prompt.id,
          prompt: prompt.prompt,
          name: prompt.name,
          is_date: prompt.is_date,
          data_type: prompt.data_type ?? (prompt.is_date ? "date" : null),
          use_image: prompt.use_image,
          no_retriever: prompt.no_retriever,
          web_search: prompt.web_search,
          traffic_light: prompt.traffic_light,
        })),
        variables: newVariables,
        name: fsEntry.name,
      }).unwrap();

      analytics.snapshotCreate(selectedBusinessReport.name, [fsEntry.name]);
      toast.success(result.snapshot_collection_id ? "SnapShot collection updated" : "SnapShot created");

      if (result.snapshot_collection_id) {
        navigate(
          `/snapshots/collections?businessReportId=${selectedBusinessReport.id}&snapshotCollectionId=${result.snapshot_collection_id}&refresh=true`,
        );
        return;
      }

      const outcomeId = result.business_report_outcome_ids[0];
      if (outcomeId) navigate(`/reports/outcome/${outcomeId}`);
    } catch (error) {
      toast.error("Error while saving");
      Sentry.captureException(error);
    }
  };

  const startGeneration = (newVariables: VariablesDict) => {
    if (selectedFsEntries.length === 1) {
      void createSingleSnapshot(newVariables);
      return;
    }

    setVariables(newVariables);
    toggleIsOpen(true);
  };

  const handleClick = () => {
    if (!selectedBusinessReport.variables.length) {
      startGeneration({});
    } else {
      setShowVariablesModal(true);
    }
  };

  const onVariablesModalClose = () => {
    setShowVariablesModal(false);
  };

  const generateReport = (newVariables: VariablesDict) => {
    setShowVariablesModal(false);
    startGeneration(newVariables);
  };

  return (
    <div className="flex flex-col items-end">
      {showVariablesModal && (
        <InjectVariablesModal
          onVariablesModalClose={onVariablesModalClose}
          businessReport={selectedBusinessReport}
          generateReport={generateReport}
        />
      )}
      {isOpen && (
        <GenerateSnapshotCollectionModal
          variables={variables}
          prompts={prompts}
          businessReport={selectedBusinessReport}
          fsEntries={selectedFsEntries}
          isOpen={isOpen}
          setIsOpen={toggleIsOpen}
        />
      )}

      <Button
        type="button"
        disabled={
          !selectedFsEntries.length || isCreatingSnapshot || (selectedFsEntries.length > MAX_FILES && !isUserAdmin)
        }
        onClick={handleClick}
      >
        <Sparkles className="mr-2 size-4" aria-hidden="true" />
        Generate SnapShot
      </Button>

      {selectedFsEntries.length > MAX_FILES && !isUserAdmin && (
        <p className="mt-2 max-w-xs text-right text-sm font-medium text-red-700">
          You cannot create a snapshot for more than {MAX_FILES} documents at once.
        </p>
      )}
    </div>
  );
};
